import { Button, Icon, Link, MenuItem } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType
} from "common/interfaces";

import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd, SearchAndAddFilterOption } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { Layout, PaneBody, PaneContent, ResourcePaneSideBar, SearchBarSort, teamResourceGridProps } from "js/react/views/SharedResources/Components";
import { WorkspaceResourceFolderHeaderActions } from "js/react/views/SharedResources/Components/WorkspaceResourceFolderMenu";

import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { getStaticUrl } from "js/config";
import SharedSlidesPaneController, { SharedSlidesItem, SharedSlidesPaneControllerState } from "js/controllers/SharedSlidesPaneController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import ux from "js/core/services/userExperiences";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { Gap20, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { themeColors } from "js/react/sharedStyles";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { SharedSlideThumbnail } from "js/react/views/SharedResources/Components/SharedSlideThumbnail";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { folderDropResourceFromWorkspaceResource, moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import CreateSharedSlideDialog from "js/react/views/UserOptions/dialogs/CreateSharedSlideDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.TEAMS);
const checkIcon = getStaticUrl("/images/auth/done-check.gif");

const ResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const SearchControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export const ALL_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE];
export const SHARED_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE];
export const SLIDE_TEMPLATES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE_TEMPLATE];

const CONTENT_FILTER_ALL = "all_content";
const TAG_FILTER_ALL = "all_tags";

type SharedSlidesPaneProps = WorkspaceControllerState & SharedSlidesPaneControllerState & {
    resourceTypes: WorkspaceResourceType[];
    isManageLibrary: boolean;
    showUpvote?: boolean;
    onSelectFolder?: (resourceTypes: WorkspaceResourceType[], folderId?: string) => void;
    onSelected?: (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[]) => void;
    folderId?: string;
};

const contentFilterOptions: SearchAndAddFilterOption[] = [
    { field: CONTENT_FILTER_ALL, label: t("All Content") },
    { field: "resource:metadata:name", label: t("Title") },
    { field: "resource:metadata:description", label: t("Description") },
    { field: "slide:text", label: t("Slide Content") },
    { field: "resource:tagsText", label: t("Tags") },
    { field: "slide:templateTitle", label: t("Template") },
];

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha",
}, {
    field: "resource.metadata.ratedByUsersCount", label: t("Rating"), direction: "desc", icon: "star",
}, {
    field: "resource.metadata.usedInPresentationsCount", label: t("Popularity"), direction: "desc", icon: "trending_up",
}, {
    field: "resource.createdAt", label: t("Created At"), direction: "desc", icon: "calendar_month",
}, {
    field: "resource.modifiedAt", label: t("Modified At"), direction: "desc", icon: "edit_calendar",
}];

const ListHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ListHeaderBreadcrumbGroup = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
`;

export function SharedSlidesPane({ resourceTypes: initialResourceTypes, tags, fetching, data, isManageLibrary, showUpvote, resourceFolders, onSelectFolder, onSelected, folderId }: SharedSlidesPaneProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [hasEverHadResults, setHasEverHadResults] = useState(false);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<SearchBarSort>({
        field: "resource.metadata.name",
        direction: "asc",
    });
    const [selectedContent, setSelectedContent] = useState(CONTENT_FILTER_ALL);
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const [selectedItems, setSelectedItems] = useState<Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[]>([]);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems<SharedSlidesItem>(item => folderDropResourceFromWorkspaceResource(item.resource));
    const [resourceTypes, setResourceTypes] = useState<WorkspaceResourceType[]>(initialResourceTypes);

    const searchResults = SharedSlidesPaneController.initialized ? SharedSlidesPaneController.legacyData : [];

    useEffect(() => {
        setResourceTypes(initialResourceTypes);
    }, [initialResourceTypes]);

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    // Refetches clear data, so data.length alone can't tell an empty workspace from one that is
    // reloading. Once slides have been seen, the workspace is known not to be empty.
    useEffect(() => {
        if (data.length > 0) {
            setHasEverHadResults(true);
        }
    }, [data.length]);

    // initialize() resolves while the first page is still loading, so the pane is only done
    // loading once that first fetch settles.
    useEffect(() => {
        if (isInitializedForCurrentMount && !fetching) {
            setHasCompletedInitialLoad(true);
        }
    }, [isInitializedForCurrentMount, fetching]);

    useEffect(() => {
        ux.addSlideToPresentation.update({ subtype: ux.Subtype[ux.Type.ADD_SLIDES_TO_PRESENTATION].ADD_SHARED_SLIDES_AND_COPY_TEAM_TEMPLATES });
    }, []);

    const controllerSort = [{
        field: sort.field,
        direction: sort.direction,
    }];

    const getControllerFilters = (nextResourceTypes: WorkspaceResourceType[]) => {
        const filters = [];

        if (nextResourceTypes.length > 0 || selectedFolderId) {
            filters.push({
                field: "resource.resourceType",
                operator: "in",
                // If a folder is selected, we always show all slides and slide templates in that folder
                value: selectedFolderId ? ALL_SLIDES_RESOURCE_TYPES : nextResourceTypes,
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            filters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            filters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        if (!isManageLibrary) {
            filters.push({
                field: "resource.metadata.isDisabled",
                operator: "ne",
                value: true
            });
        }

        return filters;
    };

    const controllerFilters = useMemo(() => getControllerFilters(resourceTypes), [resourceTypes, selectedTag, selectedFolderId, isManageLibrary]);

    const controllerSearch = useMemo(() => ({
        query,
        fields: selectedContent === CONTENT_FILTER_ALL ? undefined : [selectedContent],
    }), [query, selectedContent]);

    const tagFilterOptions = useMemo<SearchAndAddFilterOption[]>(() => [
        { field: TAG_FILTER_ALL, label: t("All Tags") },
        ...tags.map(tag => ({
            field: tag.tag,
            label: `${tag.tag} (${tag.count})`,
        })),
    ], [tags]);

    useEffect(() => {
        let isMounted = true;

        void SharedSlidesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: ALL_SLIDES_RESOURCE_TYPES,
            sort: controllerSort,
            filters: getControllerFilters(initialResourceTypes),
            search: controllerSearch,
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            SharedSlidesPaneController.reset();
        };
    }, []);

    const prevSearchStateRef = useRef({
        query,
        selectedContent,
        selectedTag,
        selectedFolderId,
        resourceTypes,
        sort,
    });

    useEffect(() => {
        if (!SharedSlidesPaneController.initialized) {
            return;
        }

        const prev = prevSearchStateRef.current;

        if (!_.isEqual(prev.sort, sort)) {
            SharedSlidesPaneController.setSort(controllerSort);
        }
        if (
            !_.isEqual(prev.resourceTypes, resourceTypes) ||
            prev.selectedTag !== selectedTag ||
            prev.selectedFolderId !== selectedFolderId
        ) {
            SharedSlidesPaneController.setFilters(controllerFilters);
        }
        if (prev.query !== query || prev.selectedContent !== selectedContent) {
            SharedSlidesPaneController.setSearch(controllerSearch);
        }

        prevSearchStateRef.current = { query, selectedContent, selectedTag, selectedFolderId, resourceTypes, sort };
    }, [query, selectedContent, selectedTag, selectedFolderId, resourceTypes, getObjectHash(sort)]);

    const showTeamSlideDialog = async (item: { id: string }) => {
        await ShowDialogAsync(TeamSlideDialog, {
            slideId: item.id,
        });
    };

    const copySlide = async (
        itemModel: SharedSlidesItem,
        updateProps: Partial<Pick<SharedSlidesItem, "name" | "tags" | "description" | "isDisabled" | "isTemplate">> = {}
    ) => {
        try {
            await withProgressDialog({
                title: t("Copying slide..."),
                action: async () => {
                    const {
                        name,
                        id,
                        tags,
                        description,
                        isDisabled,
                        isTemplate,
                    } = { ...itemModel, ...updateProps };

                    const slide = new Slide({ id });
                    await slide.load();

                    const slideModel = _.cloneDeep(slide.attributes);
                    delete slideModel.id;
                    delete slideModel.libraryItemId;
                    delete slideModel.thumbnails;
                    delete slideModel.presentationId;

                    const newSlide = new Slide(slideModel, {
                        userId: app.userId,
                        autoSync: false,
                    });
                    await newSlide.load();

                    const resource = {
                        resourceId: newSlide.id,
                        resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                        tags: tags ?? [],
                        folderIds: [],
                        metadata: {
                            name,
                            description,
                            isDisabled,
                            contentModifiedAt: newSlide.get("modifiedAt"),
                            usedByUsers: [],
                            usedInPresentations: [],
                            usedInPublicPresentations: [],
                            ratedByUsers: [],
                            createdBy: app.user.id,
                            modifiedBy: app.user.id,
                        },
                    };

                    await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

                    if (selectedFolderId) {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: selectedFolderId,
                            update: {
                                add: [{
                                    resourceId: resource.resourceId,
                                    resourceType: resource.resourceType,
                                    permissions: {
                                        read: { object: true, permissions: true },
                                        write: { object: false, permissions: false },
                                    },
                                }],
                            },
                        });
                    }
                },
            });

            return true;
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] copySlide failed");

            ShowErrorDialog({
                error: t("An error occurred while copying the slide."),
                message: (err as Error).message,
            });

            return false;
        }
    };

    const handleEditSlide = async (item: { id: string, resource: { resourceType: WorkspaceResourceType } }) => {
        AppController.showSlideEditor({ slideId: item.id, resourceType: item.resource.resourceType });
    };

    const handleCopySlide = async (item: SharedSlidesItem) => {
        if (item.isTemplate) {
            const didCopy = await copySlide(item, {
                isTemplate: false,
                name: t("Copy of {{name}}", { name: item.name }),
            });

            if (didCopy) {
                ShowMessageDialog({
                    dialogTitleProps: {
                        style: { padding: "10px 34px" },
                    },
                    buttonOptions: {
                        color: "blue"
                    },
                    title: t("A copy of this slide is now available to your team as a finished, uneditable Shared Slide."),
                    header: (<img src={checkIcon} width={75} height={75} />),
                });
            }
            return;
        }

        const didCopy = await copySlide(item, {
            isTemplate: true,
            name: t("Copy of {{name}}", { name: item.name }),
            isDisabled: false,
        });

        if (didCopy) {
            ShowMessageDialog({
                dialogTitleProps: {
                    style: { padding: "10px 34px" },
                },
                buttonOptions: {
                    color: "blue"
                },
                title: t("A copy of this slide is now available to your team as an editable Slide Template."),
                header: (<img src={checkIcon} width={75} height={75} />),
            });
        }
    };

    const handleDuplicateSlide = async (item: SharedSlidesItem) => {
        try {
            const slide = new Slide({ id: item.id });
            await slide.load();

            await ShowDialogAsync(CreateSharedSlideDialog, {
                slide,
                presentation: null,
                resourceType: item.isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                folderId: selectedFolderId,
                forceCreateNewSlide: true,
                initialName: t("Copy of {{name}}", { name: item.name }),
                initialDescription: item.description ?? "",
                initialTags: item.tags,
                initialIsDisabled: item.isDisabled,
                dialogTitle: t("Duplicate this team slide"),
                okButtonLabel: t("Duplicate"),
            });
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] duplicate slide failed", { id: item.id });

            ShowErrorDialog({
                error: t("An error occurred while duplicating the slide."),
                message: (err as Error).message,
            });
        }
    };

    const handleRemoveFromFolder = async (item: {
        id: string;
        resource: { resourceId: string; resourceType: WorkspaceResourceType };
    }) => {
        if (!selectedFolderId) {
            return;
        }

        const items = selectedItems.length > 0 && selectedItems.some(selected => selected.id === item.id)
            ? selectedItems
            : [item];
        const resources = items.map(selectedItem => ({
            resourceId: selectedItem.resource.resourceId,
            resourceType: selectedItem.resource.resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: items.length === 1 ? t("slide") : t("slides"),
        });
    };

    const handleDeleteSlide = async (item: SharedSlidesItem) => {
        const { resource: { resourceId, resourceType } } = item;
        const resourceName = resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
            ? t("Slide Template")
            : t("Shared Slide");
        const usedByCount = item.usedByCount ?? item.resource?.metadata?.usedByUsers?.length ?? 0;

        const deleteResource = async () => {
            try {
                await withProgressDialog({
                    title: t("Deleting {{resourceName}}...", { resourceName }),
                    action: () => WorkspaceController.deleteWorkspaceResource(
                        { resourceId, resourceType },
                        { trackActivity: "SharedSlideLibrary" }
                    ),
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] delete failed", { resourceId, resourceType });

                ShowErrorDialog({
                    error: t("An error occurred while deleting the Team Slide."),
                    message: (
                        <>
                            <p>
                                <strong>{t("Error")}:</strong> {(err as Error).message}
                            </p>
                            <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                        </>
                    ),
                });
            }
        };

        if (resourceType === WorkspaceResourceType.SLIDE) {
            if (await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
                message: (
                    <>
                        <Trans
                            i18nKey="Deleting a Shared Slide permanently removes it from your Team Resources. Existing instances of this slide will no longer update automatically and will be editable by presentation editors. <link>See where this slide is being used.</link>"
                            components={{
                                link: <Link onClick={() => showTeamSlideDialog(item)} />,
                            }}
                        />
                        <Gap20 />
                        <StatsContainer>
                            <FlexBox left middle>
                                <Icon>star</Icon>
                                {t("{{count}} {{likes}}", {
                                    count: item.ratedByCount,
                                    likes: item.ratedByCount === 1 ? t("like") : t("likes"),
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>person</Icon>
                                {t("Used by {{count}} {{people}}", {
                                    count: usedByCount,
                                    people: usedByCount === 1 ? t("person") : t("people"),
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>cloud_download</Icon>
                                {t("Used in {{count}} {{presentations}}", {
                                    count: item.presentationCount,
                                    presentations: item.presentationCount === 1 ? t("presentation") : t("presentations"),
                                })}
                            </FlexBox>
                        </StatsContainer>
                    </>
                ),
                okButtonLabel: t("Delete Shared Slide"),
                buttonOptions: {
                    acceptButtonColor: "red",
                },
            })) {
                await deleteResource();
            }
            return;
        }

        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
            message: t("Deleting a {{resourceName}} permanently removes it from your {{resourceName}} library and prevents it from being used in any future presentations.", { resourceName }),
            buttonOptions: { acceptButtonColor: "red" },
            okButtonLabel: t("PERMANENTLY DELETE TEAM SLIDE"),
        })) {
            await deleteResource();
        }
    };

    const handleDeleteSlides = useCallback(async (items: SharedSlidesItem[]) => {
        if (items.length === 1) {
            await handleDeleteSlide(items[0]);
            return;
        }

        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete {{count}} selected team slides?", { count: items.length }),
            message: t("Deleting these {{count}} selected team slides permanently removes them from your Team Resources. Existing instances of Shared Slides will no longer update automatically.", { count: items.length }),
            buttonOptions: { acceptButtonColor: "red" },
            okButtonLabel: t("Delete {{count}} Team Slides", { count: items.length }),
        })) {
            await withProgressDialog({
                title: t("Deleting {{count}} slides...", { count: items.length }),
                action: async () => {
                    await Promise.all(items.map(item => WorkspaceController.deleteWorkspaceResource(
                        {
                            resourceId: item.resource.resourceId,
                            resourceType: item.resource.resourceType,
                        },
                        { trackActivity: "SharedSlideLibrary" }
                    )));
                },
            });
        }
    }, [handleDeleteSlide]);

    const handleNavigateToFolder = useCallback(({ folderId: nextFolderId, subFolderId }: { folderId: string, subFolderId?: string }) => {
        const nextSelectedFolderId = subFolderId ?? nextFolderId;
        setSelectedFolderId(nextSelectedFolderId);
        setQuery("");
        onSelectFolder?.(resourceTypes, nextSelectedFolderId);
    }, [onSelectFolder, resourceTypes]);

    const handleFolderDrop = useCallback(async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            setQuery("");
            onSelectFolder?.(resourceTypes, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving slide to folder..."),
            progressTitlePlural: count => t("Moving {{count}} slides to folder...", { count }),
            errorMessage: t("An error occurred while moving slides to the folder."),
            logTag: "SharedSlidesPane",
        });
    }, [clearDragItems, getActiveDragItems, onSelectFolder, resourceTypes, selectedFolderId]);

    const handleThumbnailVisibilityChange = (isVisible: boolean, item: { resource: { id: string } }) => {
        if (!isVisible || !SharedSlidesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.resource.id);
        if (index === data.length - 1) {
            SharedSlidesPaneController.loadMore();
        }
    };

    const handleAddItem = async ({ createInFolder = false }: { createInFolder?: boolean } = {}) => {
        const resourceType = _.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)
            ? WorkspaceResourceType.SLIDE
            : WorkspaceResourceType.SLIDE_TEMPLATE;
        const folderIdForCreate = createInFolder ? selectedFolderId ?? undefined : undefined;

        // Top-level create should leave the folder view so the new slide lands at root.
        if (!createInFolder && selectedFolderId) {
            setSelectedFolderId(null);
            onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, null);
        }

        AppController.showSlideEditor({
            resourceType,
            slideId: undefined,
            folderId: folderIdForCreate,
        });
    };

    const handleAddSelectedSlides = (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[] = selectedItems) => {
        if (!onSelected) {
            return;
        }

        const analyticsProps = {
            ...ds.selection.presentation.getAnalytics(),
            slide_ids: items.map(item => item.id),
        };
        trackActivity("Presentation", "LibrarySlideAdded", null, null, analyticsProps);

        onSelected(items);
    };

    const handleSearch = (nextQuery: string) => {
        setQuery(nextQuery);
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        setSelectedFolderId(null);
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, null);
    };

    const handleSort = (nextSort: SearchBarSort) => {
        setSort(nextSort);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        setSelectedContent(event.target.value);
    };

    const handleSelectTag = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleSelectResourceType = useCallback((nextResourceTypes: WorkspaceResourceType[]) => {
        const isAllSlidesView = _.isEqual(nextResourceTypes, ALL_SLIDES_RESOURCE_TYPES);
        const isAlreadySelected = isAllSlidesView &&
            _.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES) &&
            selectedFolderId === null &&
            !folderId;

        if (isAlreadySelected) {
            return;
        }

        setResourceTypes(nextResourceTypes);
        setSelectedFolderId(null);
        if (!isAllSlidesView) {
            setQuery("");
        }
        onSelectFolder?.(nextResourceTypes, null);
    }, [folderId, onSelectFolder, resourceTypes, selectedFolderId]);

    const hasSlideFolders = useMemo(
        () => resourceFolders.some(
            folder => (
                folder.resourcesType === WorkspaceResourceType.SLIDE ||
                folder.resourcesType === WorkspaceResourceType.SLIDE_TEMPLATE
            ) && !folder.parentFolderId
        ),
        [resourceFolders]
    );

    const handleSelectSidebarFolder = useCallback((nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        setQuery("");
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, nextFolderId ?? undefined);
    }, [onSelectFolder]);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const isAllSlidesView = _.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES);

    const shouldHideTopLevelSideBar = data.length === 0 &&
        !hasEverHadResults &&
        !query &&
        selectedTag === TAG_FILTER_ALL &&
        !selectedFolderId &&
        !folderId &&
        isAllSlidesView &&
        !hasSlideFolders;
    // Sidebar and header both wait for the first fetch to settle so the loading spinner stands
    // alone and the pane chrome appears in one step instead of piece by piece.
    const shouldRenderSideBar = hasCompletedInitialLoad && !shouldHideTopLevelSideBar;
    const shouldHideTopLevelHeader = !hasCompletedInitialLoad;

    const sideBarCategoryNav = useMemo(() => (
        <>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(ALL_SLIDES_RESOURCE_TYPES)}
                id="all-slides"
            >
                <Icon>select_all</Icon>
                {t("All slides")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(SHARED_SLIDES_RESOURCE_TYPES)}
                id="shared-slides"
            >
                <Icon>link</Icon>
                {t("Shared Slides")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(SLIDE_TEMPLATES_RESOURCE_TYPES)}
                id="slide-templates"
            >
                <Icon>edit_note</Icon>
                {t("Slide Templates")}
            </SideBarItem>
        </>
    ), [handleSelectResourceType, resourceTypes, selectedFolderId]);

    const renderContextMenu = useCallback((selection: SharedSlidesItem[]) => {
        if (selection.length === 0) {
            return null;
        }

        if (selection.length > 1) {
            return null;
        }

        const item = selection[0];
        return [
            ...(canUpgradeToManageSharedResources ? [
                <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Slide...")}</MenuItem>,
                <MenuItem key="properties" disabled divider><UpgradeIcon /><Gap5 />{t("View Properties...")}</MenuItem>,
                <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
            ] : [
                <MenuItem key="edit" divider onClick={() => handleEditSlide(item)}><Icon>edit</Icon>{t("Edit Slide...")}</MenuItem>,
                <MenuItem key="properties" divider onClick={() => showTeamSlideDialog(item)}><Icon>label</Icon>{t("View Properties...")}</MenuItem>,
                <MenuItem key="duplicate" divider onClick={() => handleDuplicateSlide(item)}><Icon>file_copy</Icon>{t("Duplicate")}</MenuItem>,
            ]),
            ...(selectedFolderId ? [
                <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder(item)}>
                    <Icon>remove_circle</Icon>{t("Remove from Folder")}
                </MenuItem>
            ] : []),
            <MenuItem key="delete" onClick={() => handleDeleteSlides([item])}><Icon>delete</Icon>{t("Delete")}</MenuItem>,
        ];
    }, [
        canUpgradeToManageSharedResources,
        handleDeleteSlides,
        handleDuplicateSlide,
        handleEditSlide,
        handleRemoveFromFolder,
        selectedFolderId,
        showTeamSlideDialog,
    ]);

    const renderSlides = () => {
        const isLoadingMore = fetching && searchResults.length > 0;
        const isLoadingInitial = fetching && searchResults.length === 0;

        if (isLoadingInitial || !SharedSlidesPaneController.initialized) {
            return <Spinner />;
        }

        if (!searchResults.length) {
            if (!query) {
                if (selectedFolderId) {
                    return (<SharedSlideFolderNotice hideMessage={!isManageLibrary} />);
                }

                if (selectedTag === TAG_FILTER_ALL && !selectedFolderId) {
                    if (_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Shared Slides yet.")}
                            message={t("Create a slide once, and your team can use it infinitely. Updates will apply anywhere the slide is used.")}
                        />);
                    }
                    if (_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Slide Templates yet.")}
                            message={t("Give your team a head start with a custom library of slide templates.")}
                        />);
                    }

                    return <NoTeamSlideNotice />;
                }
            }
            return <NoMatchNotice />;
        }

        return (
            <MultiSelectThumbnailGrid
                {...teamResourceGridProps}
                items={searchResults}
                allowMultiSelect
                allowDragging={isManageLibrary && canUseSharedResources}
                thumbnailClass={SharedSlideThumbnail}
                isLoadingMore={isLoadingMore}
                thumbnailProps={{
                    isManageLibrary,
                    showUpvote,
                    canUpgradeToManageSharedResources,
                    folderId: selectedFolderId,
                    onEditSlide: handleEditSlide,
                    onDeleteSlide: handleDeleteSlide,
                    onRemoveFromFolder: selectedFolderId ? handleRemoveFromFolder : undefined,
                    onCopySlide: handleCopySlide,
                    onDuplicateSlide: handleDuplicateSlide,
                    onVisibleChange: handleThumbnailVisibilityChange,
                    renderContextMenuItems: renderContextMenu,
                    resourceFolders,
                    onNavigateToFolder: handleNavigateToFolder,
                }}
                onItemSelected={setSelectedItems}
                onStartDragItems={handleStartDragItems}
                onDropDragItems={handleDropDragItems}
                renderContextMenuItems={isManageLibrary ? renderContextMenu : undefined}
                onDoubleClick={canUpgradeToManageSharedResources ? undefined : (slide => {
                    if (isManageLibrary) {
                        showTeamSlideDialog(slide);
                    } else {
                        handleAddSelectedSlides([slide]);
                    }
                })}
            />
        );
    };

    const title = useMemo(() => {
        if (_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)) {
            return t("Shared Slides");
        }
        if (_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
            return t("Slide Templates");
        }
        return t("Slides");
    }, [resourceTypes]);

    const handleBreadcrumbSelectFolder = (nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        SharedSlidesPaneController.setSearch({ query: "" });
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, nextFolderId ?? undefined);
    };

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: title,
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleBreadcrumbSelectFolder,
    }), [title, selectedFolderId, resourceFolders]);

    return (
        <Layout>
            {canUseSharedResources && !shouldHideTopLevelHeader && (<>
                <SearchControls>
                    <SearchAndAdd
                        onSearch={handleSearch}
                        searchValue={query}
                        searchPlaceholder={t("Search slides...")}
                        contentFilterOptions={contentFilterOptions}
                        selectedContentFilter={selectedContent}
                        onContentFilterChange={handleContentFilterChange}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleSelectTag}
                        actions={isManageLibrary ? [{
                            label: t("New Slide"),
                            onClick: () => handleAddItem(),
                            variant: "primary",
                        }] : []}
                    />
                </SearchControls>
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Slides")}
                        bodyText={t("Upgrade to a Team plan to customize a searchable library of reusable templates and Shared Slides that stay synced across every deck.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_slides" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                <ResourcePaneSideBar
                    visible={shouldRenderSideBar}
                    readOnly={!isManageLibrary}
                    resourceTypes={ALL_SLIDES_RESOURCE_TYPES}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    createResourcesType={WorkspaceResourceType.SLIDE}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={handleSelectSidebarFolder}
                >
                    {sideBarCategoryNav}
                </ResourcePaneSideBar>

                <PaneContent>
                    {!shouldHideTopLevelHeader && (
                        <ListHeaderRow>
                            <ListHeaderBreadcrumbGroup>
                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                {isManageLibrary && selectedFolderId && (
                                    <WorkspaceResourceFolderHeaderActions
                                        folderId={selectedFolderId}
                                        resourceTypes={ALL_SLIDES_RESOURCE_TYPES}
                                        resourceFolders={resourceFolders}
                                        createResourcesType={WorkspaceResourceType.SLIDE}
                                        onSelectFolder={handleSelectSidebarFolder}
                                        onCreateItem={() => handleAddItem({ createInFolder: true })}
                                        showCreateButton
                                    />
                                )}
                            </ListHeaderBreadcrumbGroup>
                            {canUseSharedResources && (
                                <PresentationListHeader
                                    sortOptions={sortOptions}
                                    sort={sort}
                                    searchQuery={query}
                                    onSetSort={handleSort}
                                    showViewTypeToggle={false}
                                />
                            )}
                        </ListHeaderRow>
                    )}
                    <ResultsContainer>
                        {renderSlides()}
                    </ResultsContainer>
                    {!isManageLibrary && !fetching && SharedSlidesPaneController.legacyData.length > 0 &&
                        <AddSlideContainer>
                            <AddSlideButton disabled={!selectedItems.length} onClick={() => handleAddSelectedSlides()}>
                                {t("Add Selected Slides")}
                            </AddSlideButton>
                        </AddSlideContainer>
                    }
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(SharedSlidesPaneController.withState(SharedSlidesPane));
